import { Component, Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, errorMessage, isAuthFailure } from './api';
import { clearToken, restore, signIn, signOut, signUp } from './auth';
import { useTheme } from './theme';
import type { Session, Snapshot } from './types';
import { PublicSite } from './views/Landing';
import { Gate } from './views/Gate';
import { AdminView } from './views/Admin';
import { OfficeView } from './views/Office';
import { DriverView } from './views/Driver';
import { Btn, Empty, Icon, IconBtn, Loading, Notice, type IconName } from './components/ui';
import { docAlertCounts, unitAlerts } from './format';

type Data = Omit<Snapshot, 'session'>;

interface PortalState {
  token: string;
  session: Session;
  data: Data | null;
  busy: boolean;
  error: string | null;
  notice: string | null;
  refresh(): Promise<void>;
  /** Mutación + resincronización (mismo patrón que la app). Devuelve si se aplicó — los modales cierran solo con true. */
  mutate(action: () => Promise<unknown>, success?: string): Promise<boolean>;
  dismiss(): void;
  logout(): Promise<void>;
  /** true si `e` es un fallo de autenticación (ya se hizo logout); lo usan las
      cargas perezosas para no mostrar «Reintentar» cuando el token murió. */
  authFail(e: unknown): boolean;
  /** Manda un error al banner global — para fallos fuera de mutate() (p.ej.
      el prep asíncrono de adjuntos que corre antes de la mutación y de otro
      modo moriría como unhandled rejection sin aviso). */
  fail(e: unknown): void;
}

const Ctx = createContext<PortalState | null>(null);
export function usePortal(): PortalState {
  const value = useContext(Ctx);
  if (!value) throw new Error('PortalProvider requerido');
  return value;
}

// ---------- Navegación por secciones ----------

interface NavItem { id: string; label: string; short?: string; icon: IconName }
interface NavGroup { area: 'directorio' | 'empresa'; title: string; items: NavItem[] }

// Nav agrupada del riel (etiquetas small-caps por área) según el rol:
// admin → Directorio + Sistema; oficina → Operación + Administración + Config;
// chofer → Empresa + Operación.
const ADMIN_NAV: NavItem[] = [
  { id: 'miembros', label: 'Miembros', icon: 'people' },
  { id: 'aportaciones', label: 'Aportaciones', short: 'Aportes', icon: 'clipboard' },
  { id: 'empresas', label: 'Empresas', icon: 'building' },
  { id: 'bitacora', label: 'Bitácora', icon: 'list' },
];
const ADMIN_SYS_NAV: NavItem[] = [
  { id: 'servidor', label: 'Servidor', icon: 'pulse' },
];
const OFFICE_OPS_NAV: NavItem[] = [
  { id: 'tablero', label: 'Tablero', icon: 'grid' },
  { id: 'viajes', label: 'Viajes', icon: 'route' },
  { id: 'unidades', label: 'Unidades', icon: 'truck' },
  { id: 'documentos', label: 'Documentos', short: 'Docs', icon: 'doc' },
];
const OFFICE_ADM_NAV: NavItem[] = [
  { id: 'equipo', label: 'Equipo', icon: 'people' },
  { id: 'lugares', label: 'Lugares', icon: 'pin' },
];
const OFFICE_CFG_NAV: NavItem[] = [
  { id: 'config', label: 'Config', icon: 'sliders' },
];
const DRIVER_COMPANY_NAV: NavItem[] = [
  { id: 'empresa', label: 'Mi empresa', short: 'Empresa', icon: 'building' },
];
const DRIVER_OPS_NAV: NavItem[] = [
  { id: 'viajes', label: 'Mis viajes', short: 'Viajes', icon: 'route' },
];

/** La sección activa viaja en ?v= para recarga y deep-link (spa estático). */
const readView = () => new URLSearchParams(window.location.search).get('v') ?? '';

/** El riel de escritorio se puede plegar a iconos; la preferencia se guarda. */
const RAIL_KEY = 'cargados-rail';
const readRailMini = () => { try { return localStorage.getItem(RAIL_KEY) === 'mini'; } catch { return false; } };

export default function App() {
  const [phase, setPhase] = useState<'loading' | 'anon' | 'authed'>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [data, setData] = useState<Data | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const inFlight = useRef(false);

  const drop = useCallback(() => {
    clearToken(); setToken(null); setSession(null); setData(null); setPhase('anon');
    // La URL también se limpia: la siguiente sesión no debe heredar la sección
    // ni un deep-link (?t=/?u= reabrirían un modal de datos que no existen).
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* contextos sin historial */ }
  }, []);

  /** session + (si la membresía está activa y verificada) snapshot completo. */
  const load = useCallback(async (tok: string) => {
    const me = await api<Session>('session', {}, tok);
    setSession(me);
    if (me.status === 'active' && me.emailVerified) {
      const snap = await api<Snapshot>('snapshot', {}, tok);
      setSession(snap.session);
      const { session: _s, ...rest } = snap;
      setData(rest);
    } else {
      setData(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    const tok = token;
    if (!tok) return;
    try { await load(tok); setError(null); }
    catch (e) {
      if (isAuthFailure(e)) { drop(); return; }
      setError(errorMessage(e));
    }
  }, [token, load, drop]);

  /** true si `e` es un fallo de autenticación — ya se llamó drop() y la
      pestaña va a login; lo usan también las cargas perezosas de las vistas
      (bitácora, lecturas, docs del viaje) para no quedar en «Reintentar»
      eterno cuando el token murió a media sesión. */
  const authFail = useCallback((e: unknown) => {
    if (!isAuthFailure(e)) return false;
    drop();
    return true;
  }, [drop]);

  /** Errores fuera de mutate() (prep de archivos/fotos antes de la llamada):
      sin esto morían como unhandled rejection sin aviso visible. */
  const fail = useCallback((e: unknown) => { setError(errorMessage(e)); }, []);

  // Restaura la pestaña al abrir: el cst_ de sessionStorage se valida con `session`.
  useEffect(() => {
    let alive = true;
    restore()
      .then(async found => {
        if (!alive) return;
        if (!found) { setPhase('anon'); return; }
        try { await load(found.token); if (!alive) return; setToken(found.token); setPhase('authed'); }
        catch (e) {
          if (!alive) return;
          if (isAuthFailure(e)) { drop(); return; }
          setToken(found.token); setPhase('authed'); setError(errorMessage(e));
        }
      })
      .catch(() => { if (alive) setPhase('anon'); });
    return () => { alive = false; };
  }, [load, drop]);

  const mutate = useCallback(async (action: () => Promise<unknown>, success?: string): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true; setBusy(true); setError(null); setNotice(null);
    try {
      await action();
      const tok = token;
      // La mutación ya quedó: un fallo del sync posterior se reporta suave y no niega el éxito.
      if (tok) { try { await load(tok); } catch (e) { if (isAuthFailure(e)) { drop(); return false; } setError(errorMessage(e)); } }
      if (success) setNotice(success);
      return true;
    } catch (e) {
      if (isAuthFailure(e)) { drop(); return false; }
      setError(errorMessage(e));
      return false;
    } finally { inFlight.current = false; setBusy(false); }
  }, [token, load, drop]);

  const doSignIn = useCallback(async (identifier: string, password: string) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { token: tok, session: me } = await signIn(identifier, password);
      setToken(tok); setSession(me); setPhase('authed');
      if (me.status === 'active' && me.emailVerified) {
        try {
          const snap = await api<Snapshot>('snapshot', {}, tok);
          setSession(snap.session);
          const { session: _s, ...rest } = snap;
          setData(rest);
        } catch (e) { setError(errorMessage(e)); }
      }
    } catch (e) {
      // Si algo falló tras emitir el cst_ (p. ej. `session`), el token no debe quedar
      // guardado a medias: la pestaña vuelve a pedir credenciales limpio.
      clearToken();
      setToken(null); setSession(null); setData(null); setPhase('anon');
      setError(errorMessage(e));
      throw e;
    } finally { setBusy(false); }
  }, []);

  const doSignUp = useCallback(async (email: string, password: string, displayName: string) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { token: tok, session: me } = await signUp(email, password, displayName);
      setToken(tok); setSession(me); setPhase('authed');
      // La cuenta recién creada nunca está verificada: cae en Gate (código de
      // correo → joinGroup → pendiente) en vez de pedir el snapshot.
      setData(null);
    } catch (e) {
      // Igual que doSignIn: si algo falló tras emitir el cst_ no se guarda a
      // medias — la pestaña vuelve a pedir credenciales limpio.
      clearToken();
      setToken(null); setSession(null); setData(null); setPhase('anon');
      setError(errorMessage(e));
      throw e;
    } finally { setBusy(false); }
  }, []);

  const logout = useCallback(async () => {
    setBusy(true);
    try { await signOut(token); } finally { drop(); setBusy(false); }
  }, [token, drop]);

  if (phase === 'loading') return <div className="page"><Loading label="Conectando…" /></div>;
  if (phase === 'anon' || !session || !token) {
    return <PublicSite
      onSignIn={doSignIn} onSignUp={doSignUp}
      busy={busy} error={error} notice={notice}
      dismiss={() => { setError(null); setNotice(null); }} />;
  }

  // Membresía sin activar: pantallas de espera (misma voz que la app).
  const gated = !session.emailVerified || session.status !== 'active';

  return (
    <Ctx.Provider value={{
      token, session, data, busy, error, notice,
      refresh, mutate, logout, authFail, fail,
      dismiss: () => { setError(null); setNotice(null); },
    }}>
      {gated ? <GatedPage /> : <PortalShell />}
    </Ctx.Provider>
  );
}

/** Banners de error/aviso compartidos: flotan fijos sobre toda la UI
    (.toasts, z 60) — dentro del flujo un modal los tapaba por completo. */
function Banners() {
  const { error, notice, dismiss } = usePortal();
  if (!error && !notice) return null;
  return <div className="toasts">
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    {notice && <Notice tone="info" onClose={dismiss}>{notice}</Notice>}
  </div>;
}

/** Badges de rol/empresa de la sesión (topbar móvil y riel). */
function SessionBadges() {
  const { session } = usePortal();
  return <>
    {session.role === 'admin' && <span className="badge info">Administrador</span>}
    {session.companyRole === 'office' && <span className="badge ok">Oficina{session.companyName ? ` · ${session.companyName}` : ''}</span>}
    {session.companyRole !== 'office' && session.companyName && <span className="badge muted">{session.companyName}</span>}
  </>;
}

/** Marca + nombre de la sesión, usado en la topbar móvil y el riel. */
function BrandBlock({ subtitle }: { subtitle: string }) {
  return <>
    <span className="brand-mark">◆</span>
    <div className="who">
      <div className="name">Cargados.mx</div>
      <div className="tiny muted ellipsis">{subtitle}</div>
    </div>
  </>;
}

/** Sesiones sin acceso aún (correo sin verificar, pendiente, suspendido):
    topbar siempre visible, sin navegación por secciones. */
function GatedPage() {
  const { session, busy, logout } = usePortal();
  const [theme, toggleTheme] = useTheme();
  return <div className="page">
    <header className="topbar always">
      <BrandBlock subtitle={session.displayName || session.email} />
      <span className="spacer" />
      <SessionBadges />
      <IconBtn label={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'} icon={theme === 'dark' ? 'sun' : 'moon'} onClick={toggleTheme} />
      <IconBtn label="Cerrar sesión" icon="logout" busy={busy} onClick={() => { void logout(); }} />
    </header>
    <main className="wrap narrow">
      <Banners />
      <Gate />
    </main>
  </div>;
}

/** La sesión está activa pero el snapshot no llegó (red o servicio): no dejar
    al usuario en un spinner eterno — el banner ya trae el motivo. */
function LoadFallback() {
  const { busy, refresh } = usePortal();
  return <div className="card">
    <Empty icon="pulse" title="No pudimos leer tus datos"
      message="Si el servicio está reiniciando, vuelve a intentar en un momento."
      action={<Btn tone="secondary" busy={busy} onClick={() => void refresh()}>Reintentar</Btn>} />
  </div>;
}

/** Límite de error por sección: si una vista truena al renderizar (dato
    inesperado, bug), el portal muestra una tarjeta con reintento en vez de
    morir en pantalla blanca. `key={current}` ya remonta al cambiar de
    sección, así que navegar a otra pestaña también recupera el portal. */
class ViewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.error('[portal] vista falló:', err); }
  render() {
    if (!this.state.failed) return this.props.children;
    return <div className="card">
      <Empty icon="alert" title="Algo se rompió en esta pantalla"
        message="Tu información está a salvo — es un problema de interfaz."
        action={<Btn tone="secondary" onClick={() => window.location.reload()}>Recargar portal</Btn>} />
    </div>;
  }
}

/** Estructura del portal: riel lateral en escritorio, barra inferior en
    celular; la sección activa viaja en ?v= (recarga y deep-link). */
function PortalShell() {
  const { session, data, busy, logout } = usePortal();
  const [theme, toggleTheme] = useTheme();

  // Roles: admin del directorio, oficina de empresa, o chofer simple.
  const admin = session.role === 'admin';
  const office = session.companyRole === 'office' && session.status === 'active';

  // El área «empresa» siempre existe: oficina si el rol lo da, chofer si no
  // (antes eran pestañas Directorio/Mi empresa; ahora son grupos del riel).
  // Grupos del riel por rol: el área decide la vista (directorio→admin,
  // empresa→oficina/chofer); el título es la etiqueta small-caps de la sección.
  const groups = useMemo<NavGroup[]>(() => {
    const list: NavGroup[] = [];
    if (admin) list.push(
      { area: 'directorio', title: 'Directorio', items: ADMIN_NAV },
      { area: 'directorio', title: 'Sistema', items: ADMIN_SYS_NAV });
    if (office) list.push(
      { area: 'empresa', title: 'Operación', items: OFFICE_OPS_NAV },
      { area: 'empresa', title: 'Administración', items: OFFICE_ADM_NAV },
      { area: 'empresa', title: 'Config', items: OFFICE_CFG_NAV });
    else list.push(
      { area: 'empresa', title: 'Empresa', items: DRIVER_COMPANY_NAV },
      { area: 'empresa', title: 'Operación', items: DRIVER_OPS_NAV });
    return list;
  }, [admin, office]);

  const flat = useMemo(() => groups.flatMap(g => g.items), [groups]);
  const [wanted, setWanted] = useState(readView);
  // Sección efectiva: la pedida si existe para este rol, si no la primera —
  // cubre un rol que desaparece a media sesión (oficina → chofer) sin mostrar
  // lo prohibido, y el ascenso chofer → oficina cae en su Tablero.
  const current = flat.some(i => i.id === wanted) ? wanted : flat[0].id;

  // Dirección de la entrada: la sección nueva contra la anterior dentro del
  // riel (flat ya es la lista plana de secciones visibles para este rol).
  // Avanzar en el orden entra por la derecha (view-fwd), retroceder por la
  // izquierda (view-back); sin dirección clara — primera carga o sección
  // previa fuera del riel (cambio de rol a media sesión) — entra neutra.
  const prevSection = useRef<string | null>(null);
  const fromIdx = prevSection.current ? flat.findIndex(i => i.id === prevSection.current) : -1;
  const toIdx = flat.findIndex(i => i.id === current);
  const dir = fromIdx < 0 || toIdx < 0 || fromIdx === toIdx
    ? '' : toIdx > fromIdx ? ' view-fwd' : ' view-back';
  useEffect(() => { prevSection.current = current; }, [current]);

  // Canonicaliza la URL: sin ?v= o con una sección que ya no aplica → ?v=<actual>
  // con replaceState (no ensucia el historial).
  useEffect(() => {
    if (wanted === current) return;
    const params = new URLSearchParams(window.location.search);
    params.set('v', current);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    setWanted(current);
  }, [wanted, current]);

  // Atrás/adelante del navegador recorre las secciones visitadas; la vista
  // nueva arranca desde arriba (el pushState de go() ya hace scrollTo).
  useEffect(() => {
    const onPop = () => { setWanted(readView()); window.scrollTo({ top: 0 }); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((id: string, extra?: Record<string, string>) => {
    const params = new URLSearchParams(window.location.search);
    // Los deep-links (t=viaje, u=unidad) solo viajan cuando vienen en `extra`:
    // si sobrevivieran de una sección a otra, al volver el modal reaparecería
    // solo sobre datos que ya cambiaron («El viaje ya no existe»).
    params.delete('t'); params.delete('u');
    params.set('v', id);
    // Deep-link con objetivo (p.ej. { u: unitId } desde el feed del Tablero):
    // viaja en la URL — sobrevive al remonte de .view por key={current}, a
    // refresh y se puede compartir. La vista lo consume y lo limpia.
    for (const [k, v] of Object.entries(extra ?? {})) params.set(k, v);
    if (id !== current) window.history.pushState(null, '', `${window.location.pathname}?${params.toString()}`);
    else window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    if (id !== current) { setWanted(id); window.scrollTo({ top: 0 }); }
  }, [current]);

  const group = groups.find(g => g.items.some(i => i.id === current)) ?? groups[0];
  const view = group.area === 'directorio' ? 'admin' : office ? 'office' : 'driver';

  const [railMini, setRailMini] = useState(readRailMini);
  const toggleRail = () => setRailMini(m => {
    const next = !m;
    try { localStorage.setItem(RAIL_KEY, next ? 'mini' : 'full'); } catch { /* sin persistencia */ }
    return next;
  });

  // Unidades con algo por atender (taller, servicio vencido, documentos,
  // reportes): el badge del menú y la tabbar avisan sin abrir la sección.
  const unitAlertCount = useMemo(() => (data?.companyUnits ?? [])
    .filter(u => unitAlerts(u, data?.companyDocuments ?? [], data?.companyUnitReports ?? [], data?.companyUnitServices ?? []).length > 0)
    .length, [data?.companyUnits, data?.companyDocuments, data?.companyUnitReports, data?.companyUnitServices]);
  // Documentos vigentes por atender, de cualquier sujeto (unidad, operador o
  // empresa): prende el badge del menú Documentos.
  const docAlertCount = useMemo(() => {
    const c = docAlertCounts(data?.companyDocuments ?? []);
    return c.expired + c.expiring + c.rejected + c.pending;
  }, [data?.companyDocuments]);

  const navlink = (item: NavItem) => (
    <button key={item.id} type="button"
      className={`navlink${current === item.id ? ' on' : ''}`}
      aria-current={current === item.id ? 'page' : undefined}
      title={railMini ? item.label : undefined}
      onClick={() => go(item.id)}>
      <Icon name={item.icon} /><span className="lbl">{item.label}</span>
      {item.id === 'unidades' && unitAlertCount > 0 && <span className="nav-badge" aria-label={`${unitAlertCount} unidades por atender`}>{unitAlertCount}</span>}
      {item.id === 'documentos' && docAlertCount > 0 && <span className="nav-badge" aria-label={`${docAlertCount} documentos por atender`}>{docAlertCount}</span>}
    </button>
  );

  return <div className={`shell${railMini ? ' rail-mini' : ''}`}>
    {/* Escritorio ≥900 px: riel lateral fijo con marca, secciones y sesión;
        se pliega a solo iconos con el botón del pie. */}
    <aside className="rail" aria-label="Secciones">
      <div className="rail-brand">
        <span className="brand-mark">◆</span>
        <div className="who"><div className="name">Cargados.mx</div><div className="tiny muted">Portal</div></div>
      </div>
      <nav className="rail-nav">
        {groups.map(g => <div key={`${g.area}:${g.title}`}>
          <div className="nav-group">{g.title}</div>
          {g.items.map(navlink)}
        </div>)}
      </nav>
      <div className="rail-user">
        <div className="who">
          <div className="name small ellipsis">{session.displayName || session.email}</div>
          <div className="badges"><SessionBadges /></div>
        </div>
        <div className="row between">
          <IconBtn label={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'} icon={theme === 'dark' ? 'sun' : 'moon'} onClick={toggleTheme} />
          <IconBtn className="only-mini" label="Cerrar sesión" icon="logout" busy={busy} onClick={() => { void logout(); }} />
          <Btn tone="ghost" small busy={busy} onClick={() => { void logout(); }}>Cerrar sesión</Btn>
        </div>
      </div>
      <div className="rail-foot">
        <IconBtn label={railMini ? 'Expandir menú' : 'Ocultar menú'} icon="panelLeft" onClick={toggleRail} />
      </div>
    </aside>

    <div className="main">
      {/* Celular <900 px: topbar conserva marca y sesión; el riel se vuelve barra. */}
      <header className="topbar">
        <BrandBlock subtitle={session.displayName || session.email} />
        <span className="spacer" />
        <SessionBadges />
        <IconBtn label={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'} icon={theme === 'dark' ? 'sun' : 'moon'} onClick={toggleTheme} />
        <IconBtn label="Cerrar sesión" icon="logout" busy={busy} onClick={() => { void logout(); }} />
      </header>
      <main className="content">
        <Banners />
        {/* key={current} remonta la vista al cambiar de sección: la animación
            .view (styles.css) entra de nuevo — fade + barrido lateral de
            10 px según la dirección, o subida de 6 px si no hay dirección. */}
        <div key={current} className={`view${dir}`}>
          <ViewBoundary key={current}>
            {!data ? <LoadFallback />
              : view === 'admin' ? <AdminView section={current} />
              : view === 'office' ? <OfficeView section={current} go={go} />
              : <DriverView section={current} />}
          </ViewBoundary>
        </div>
      </main>
    </div>

    <nav className="tabbar" aria-label="Secciones">
      {groups.map((g, gi) => <Fragment key={`${g.area}:${g.title}`}>
        {gi > 0 && g.area !== groups[gi - 1].area && <span className="tab-sep" />}
        {g.items.map(item => (
          <button key={item.id} type="button"
            className={`navlink${current === item.id ? ' on' : ''}`}
            aria-current={current === item.id ? 'page' : undefined}
            onClick={() => go(item.id)}>
            <Icon name={item.icon} /><span>{item.short ?? item.label}</span>
            {/* Mismos pendientes del riel — sin esto en el teléfono no se veía
                que hay unidades o documentos por atender. */}
            {item.id === 'unidades' && unitAlertCount > 0 && <span className="nav-badge" aria-label={`${unitAlertCount} unidades por atender`}>{unitAlertCount}</span>}
            {item.id === 'documentos' && docAlertCount > 0 && <span className="nav-badge" aria-label={`${docAlertCount} documentos por atender`}>{docAlertCount}</span>}
          </button>
        ))}
      </Fragment>)}
    </nav>
  </div>;
}
